
/* ==========================================================================
   RUNTIME DIAGNOSTICS & SMALL SAFETY HELPERS (/database/runtime.js)
   ========================================================================== */

const onceKeys = new Set();

export function diagnosticsEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage?.getItem('sff-debug') === '1' || /(?:^|[?&])sff_debug=1(?:&|$)/.test(window.location?.search || '');
  } catch (err) {
    return false;
  }
}

export function runtimeNow() {
  return (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now()
    : Date.now();
}

export function debugPerformanceMetric(scope, startedAt, details = null) {
  if (!diagnosticsEnabled()) return 0;
  const elapsed = Math.max(0, runtimeNow() - Number(startedAt || 0));
  const suffix = details && typeof details === 'object' ? ` ${JSON.stringify(details)}` : '';
  console.debug(`[SFF PERF] ${scope}: ${elapsed.toFixed(2)}ms${suffix}`);
  return elapsed;
}

export function scheduleIdleTask(callback, timeout = 1500) {
  if (typeof callback !== 'function') return null;
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    return window.requestIdleCallback(() => callback(), { timeout });
  }
  return setTimeout(callback, Math.min(timeout, 250));
}

export function reportRuntimeIssue(scope, error, level = 'warn') {
  const message = error instanceof Error ? error.message : String(error ?? 'Unknown runtime issue');
  const logger = level === 'error' ? console.error : (level === 'info' ? console.info : console.warn);
  logger(`[SFF ${scope}] ${message}`, error instanceof Error ? error : '');
}

export function reportRuntimeIssueOnce(key, scope, error, level = 'warn') {
  if (onceKeys.has(key)) return;
  onceKeys.add(key);
  reportRuntimeIssue(scope, error, level);
}

export function debugRuntimeIssue(scope, error) {
  if (!diagnosticsEnabled()) return;
  const message = error instanceof Error ? error.message : String(error ?? 'Unknown runtime issue');
  console.debug(`[SFF ${scope}] ${message}`, error instanceof Error ? error : '');
}

export function withTimeout(promise, timeoutMs, timeoutMessage = 'Operation timed out') {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    })
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

export function stableKey(parts) {
  return parts.map(v => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'number') return Number.isFinite(v) ? String(Math.round(v * 1000) / 1000) : '';
    if (typeof v === 'boolean') return v ? '1' : '0';
    return String(v).trim().toLowerCase();
  }).join('|');
}
